import * as React from 'react';
import { Calendar } from '@components/amc/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { ScrollArea, ScrollBar } from '@components/ui/scroll-area';
import { DateTime } from 'luxon';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@components/amc/Button';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function DateTimePicker({
  defaultDateTime = null,
  defaultOpen = false,
  disabled = null,
  disabledPopoverTrigger = false,
  onTimeChange,
  renderInput,
  onClose = null,
  onSubmit = null
}) {
  const [date, setDate] = useState(defaultDateTime ?? null);
  const [initialDate, setInitialDate] = useState(defaultDateTime ?? null);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  // Popover가 열린 직후 외부 클릭으로 오인되어 닫히는 것을 방지하기 위한 플래그
  const ignoreOutsideClickRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      ignoreOutsideClickRef.current = true;
      const timer = setTimeout(() => {
        ignoreOutsideClickRef.current = false;
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  const isFirstActiveDate = date => {
    if (!disabled || !disabled.before) return false;
    return date.hasSame(disabled.before, 'day');
  };
  const ceilToNearestMinutes = (dateTime, increment = 5) => {
    const shouldAddIncrement = dateTime.minute % increment === 0;
    const roundedMinutes = Math.ceil(dateTime.minute / increment) * increment + (shouldAddIncrement ? increment : 0);
    return dateTime.set({
      minute: roundedMinutes >= 60 ? 0 : roundedMinutes,
      hour: dateTime.hour + (roundedMinutes >= 60 ? 1 : 0),
      second: 0,
      millisecond: 0
    });
  };
  const isBeforeActiveStartTime = (hour, minute) => {
    const targetTime = DateTime.local().set({
      hour: hour,
      minute: minute,
      second: 0,
      millisecond: 0
    });
    const disabledHour = disabled.before.set({
      minute: 0,
      second: 0,
      millisecond: 0
    });
    return {
      isHourBefore: targetTime < disabledHour,
      isHourMinBefore: targetTime < disabled.before
    };
  };
  const setUpNewDateTime = newDateTime => {
    setDate(newDateTime);
    onTimeChange?.(newDateTime);
  };
  const hours = Array.from({
    length: 24
  }, (_, i) => i);
  const handleDateSelect = selectedDate => {
    if (selectedDate) {
      const selectedDateTime = DateTime.fromJSDate(selectedDate);
      if (isFirstActiveDate(selectedDateTime)) {
        const newSelectedDateTime = ceilToNearestMinutes(disabled.before);
        setUpNewDateTime(newSelectedDateTime);
      } else {
        setUpNewDateTime(DateTime.fromJSDate(selectedDate));
      }
    }
  };
  const handleTimeChange = (type, value) => {
    if (date) {
      let newDate = null;
      if (type === 'hour') {
        newDate = date.set({
          hour: value
        });
      } else if (type === 'minute') {
        newDate = date.set({
          minute: value
        });
      }
      setUpNewDateTime(newDate);
    }
  };
  const handleChangeOpen = open => {
    setIsOpen(open);
    if (open && !date) {
      const now = ceilToNearestMinutes(DateTime.now());
      setUpNewDateTime(now);
    } else if (!open) {
      onClose?.();
    }
  };
  const handleCancel = () => {
    setIsOpen(false);
    onClose?.();
  };
  const handleSubmit = () => {
    if (date) {
      setIsOpen(false);
      setInitialDate(date);
      onSubmit?.(date);
    }
  };
  const hasChanges = React.useMemo(() => {
    if (!date && !initialDate) return false;
    if (!date || !initialDate) return true;
    return !date.equals(initialDate);
  }, [date, initialDate]);
  return /*#__PURE__*/_jsxs(Popover, {
    open: isOpen,
    onOpenChange: handleChangeOpen,
    children: [/*#__PURE__*/_jsx(PopoverTrigger, {
      asChild: true,
      disabled: disabledPopoverTrigger,
      children: renderInput({
        isOpen,
        date
      })
    }), /*#__PURE__*/_jsx(PopoverContent, {
      className: "w-auto p-0 bg-white rounded-lg",
      onInteractOutside: e => {
        // Popover가 열린 직후에는 잠깐 외부 클릭을 무시
        if (ignoreOutsideClickRef.current) {
          e.preventDefault();
          return;
        }
      },
      children: /*#__PURE__*/_jsxs("div", {
        className: "border border-solid rounded-lg border-my-gray-70",
        style: {
          boxShadow: '0px 8px 24px 0px rgba(23, 32, 42, 0.16)'
        },
        children: [/*#__PURE__*/_jsxs("div", {
          className: "sm:flex h-[23.75rem] rounded-lg",
          children: [/*#__PURE__*/_jsx(Calendar, {
            mode: "single",
            selected: date ? date.toJSDate() : null,
            onSelect: handleDateSelect,
            initialFocus: true,
            disabled: {
              before: disabled.before ? disabled.before.startOf('day').toJSDate() : null,
              after: disabled.after ? disabled.after.endOf('day').toJSDate() : null
            }
          }), /*#__PURE__*/_jsxs("div", {
            className: "flex flex-col divide-y sm:flex-row sm:h-full sm:divide-y-0 sm:divide-x",
            children: [/*#__PURE__*/_jsxs(ScrollArea, {
              className: "w-64 py-4 sm:w-auto",
              children: [/*#__PURE__*/_jsx("div", {
                className: "flex px-2 sm:flex-col",
                children: hours.map(hour => {
                  const isDisabledHour = date && isFirstActiveDate(date) && isBeforeActiveStartTime(hour, date.minute).isHourBefore;
                  return /*#__PURE__*/_jsx("button", {
                    className: `flex items-center justify-center w-10 h-10 rounded-lg text-label-md-500 ${date ? isDisabledHour ? 'text-my-gray-70 cursor-default' : parseInt(date.toFormat('H')) === hour ? 'bg-my-blue-50 text-my-gray-100 cursor-pointer' : 'text-my-gray-30 cursor-pointer' : 'text-my-gray-30 cursor-pointer'}`,
                    onClick: isDisabledHour ? null : () => handleTimeChange('hour', hour),
                    children: hour
                  }, hour);
                })
              }), /*#__PURE__*/_jsx(ScrollBar, {
                orientation: "horizontal",
                className: "sm:hidden"
              })]
            }), /*#__PURE__*/_jsxs(ScrollArea, {
              className: "w-64 py-4 border-l border-solid sm:w-auto border-my-gray-80",
              children: [/*#__PURE__*/_jsx("div", {
                className: "flex px-2 sm:flex-col",
                children: Array.from({
                  length: 12
                }, (_, i) => i * 5).map(minute => {
                  const isDisabledMin = date && isFirstActiveDate(date) && isBeforeActiveStartTime(date.hour, minute).isHourMinBefore;
                  return /*#__PURE__*/_jsx("button", {
                    className: `flex items-center justify-center w-10 h-10 rounded-lg text-label-md-500 cursor-pointer ${date ? isDisabledMin ? 'text-my-gray-70 cursor-default' : date.minute === minute ? 'bg-my-blue-50 text-my-gray-100 cursor-pointer' : 'text-my-gray-30 cursor-pointer' : 'text-my-gray-30 cursor-pointer'}`,
                    onClick: isDisabledMin ? null : () => handleTimeChange('minute', minute),
                    children: minute < 10 ? `0${minute}` : minute
                  }, minute);
                })
              }), /*#__PURE__*/_jsx(ScrollBar, {
                orientation: "horizontal",
                className: "sm:hidden"
              })]
            })]
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "flex justify-end gap-2 px-4 py-3 border-t border-solid border-my-gray-80",
          children: [/*#__PURE__*/_jsx(Button, {
            variant: "Outline",
            size: "Medium",
            text: "\uCDE8\uC18C",
            onClick: handleCancel
          }), /*#__PURE__*/_jsx(Button, {
            variant: "Default",
            size: "Medium",
            text: "\uC800\uC7A5",
            disabled: !date || !hasChanges,
            onClick: handleSubmit
          })]
        })]
      })
    })]
  });
}